import type { RegionId } from "./regions";

export type CommercialModel = "capex" | "opex";

export type EnbrosPricingTierId =
  | "core-wedge"
  | "expanded-chain"
  | "departmental-pod"
  | "multi-pod"
  | "enterprise";

export type EnbrosPricingTier = {
  id: EnbrosPricingTierId;
  /** Architecture label shown in UI (e.g. Core Wedge). */
  archLabel: string;
  minHeadcount: number;
  /** Inclusive upper bound; omit for open-ended top tier. */
  maxHeadcount: number | null;
  buildCostGbp: number;
  buildCostUsd: number;
};

/** TVM premium applied to OpEx lease-to-own total. */
export const OPEX_TVM_PREMIUM = 1.1;

/**
 * Enbros build-cost tiers by headcount.
 * Frontend slider may cap at 50; lookup still scales for larger values.
 */
export const ENBROS_PRICING_TIERS: readonly EnbrosPricingTier[] = [
  {
    id: "core-wedge",
    archLabel: "Core Wedge",
    minHeadcount: 1,
    maxHeadcount: 15,
    buildCostGbp: 20_000,
    buildCostUsd: 25_000,
  },
  {
    id: "expanded-chain",
    archLabel: "Expanded Chain",
    minHeadcount: 16,
    maxHeadcount: 40,
    buildCostGbp: 30_000,
    buildCostUsd: 35_000,
  },
  {
    id: "departmental-pod",
    archLabel: "Departmental Pod",
    minHeadcount: 41,
    maxHeadcount: 100,
    buildCostGbp: 40_000,
    buildCostUsd: 50_000,
  },
  {
    id: "multi-pod",
    archLabel: "Multi-Pod Deployment",
    minHeadcount: 101,
    maxHeadcount: 250,
    buildCostGbp: 60_000,
    buildCostUsd: 75_000,
  },
  {
    id: "enterprise",
    archLabel: "Enterprise Deployment",
    minHeadcount: 251,
    maxHeadcount: null,
    buildCostGbp: 80_000,
    buildCostUsd: 100_000,
  },
] as const;

export type CommercialInvestmentResult = {
  tier: EnbrosPricingTier;
  baseBuildCost: number;
  opexTotal: number;
  monthly12: number;
  monthly24: number;
  /** Bleed − base build cost (Year 1 CapEx basis). */
  year1NetSavings: number;
  capexRoiPercent: number;
  opexRoiPercent: number;
};

function toNonNegative(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function getEnbrosPricingTier(headcount: number): EnbrosPricingTier {
  const n = Math.max(0, Math.floor(toNonNegative(headcount)));
  const fallback = ENBROS_PRICING_TIERS[0]!;
  if (n < 1) {
    return fallback;
  }
  for (const tier of ENBROS_PRICING_TIERS) {
    const withinMax = tier.maxHeadcount === null || n <= tier.maxHeadcount;
    if (n >= tier.minHeadcount && withinMax) {
      return tier;
    }
  }
  return ENBROS_PRICING_TIERS[ENBROS_PRICING_TIERS.length - 1] ?? fallback;
}

export function getBaseBuildCost(
  headcount: number,
  regionId: RegionId | null | undefined,
): number {
  const tier = getEnbrosPricingTier(headcount);
  return regionId === "us" ? tier.buildCostUsd : tier.buildCostGbp;
}

/**
 * Commercial investment figures for CapEx / OpEx views.
 * ROI CapEx uses base build cost; OpEx ROI uses TVM-inflated total as denominator,
 * while Year 1 net savings always uses Bleed − base build (CapEx basis).
 */
export function calculateCommercialInvestment(
  annualBleed: number,
  headcount: number,
  regionId: RegionId | null | undefined,
): CommercialInvestmentResult {
  const tier = getEnbrosPricingTier(headcount);
  const baseBuildCost = getBaseBuildCost(headcount, regionId);
  const opexTotal = baseBuildCost * OPEX_TVM_PREMIUM;
  const bleed = toNonNegative(annualBleed);
  const year1NetSavings = bleed - baseBuildCost;
  const capexRoiPercent =
    baseBuildCost > 0 ? (year1NetSavings / baseBuildCost) * 100 : 0;
  const opexRoiPercent =
    opexTotal > 0 ? (year1NetSavings / opexTotal) * 100 : 0;

  return {
    tier,
    baseBuildCost,
    opexTotal,
    monthly12: opexTotal / 12,
    monthly24: opexTotal / 24,
    year1NetSavings,
    capexRoiPercent,
    opexRoiPercent,
  };
}

/** Whole-number ROI percent for display (e.g. 11996%). */
export function formatRoiPercent(value: number): string {
  if (!Number.isFinite(value)) return "0%";
  return `${Math.round(value)}%`;
}
